import React, { useEffect, useState } from 'react';

import { DeleteRounded, LinkRounded } from '@mui/icons-material';
import {
  Stack,
  useMediaQuery, useTheme,
} from '@mui/material';
import _ from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';

import AdditionModal from '../../components/AdditionModal';
import Breadcrumbs from '../../components/Breadcrumbs';
import CommitForm from '../../components/CommitForm';
import ContentGroupsList from '../../components/ContentGroupsList';
import ContentWrapper from '../../components/ContentWrapper';
import CopyButton from '../../components/CopyButton';
import DetailsModal, { Values } from '../../components/DetailsModal';
import Modal from '../../components/Modal';
import YouTubeVideo from '../../components/YouTubeVideo';
import {
  useDeleteVideoMutation,
  useGetVideoQuery,
  useGetVideosQuery,
  useUpdateVideoMutation,
} from '../../services/videos';
import { Resource, ShortVideo } from '../../services/videos/types';
import { SUBJECTS } from '../../utils/common/constants';
import useError from '../../utils/react/hooks/useError';
import useSuccess from '../../utils/react/hooks/useSuccess';

import {
  ActionButton,
  ColumnContainer,
  DetailsColumn,
  PageContainer,
  VideoColumn,
} from './index.styles';

const INITIAL_RESOURCE_FORM = {
  label: '',
  href: '',
};

const AdminVideoPage = () => {
  const { subject = '', videoId = '' } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const [addRelatedVideoModalOpen, setAddRelatedVideoModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [relatedVideoSearch, setRelatedVideoSearch] = useState<string>('');

  const [newResourceFormOpen, setNewResourceFormOpen] = useState<boolean>(false);
  const [newResourceForm, setNewResourceForm] = useState(INITIAL_RESOURCE_FORM);

  const { data: video, isLoading } = useGetVideoQuery({ subject, videoId });
  const {
    data: queriedVideos,
    refetch: refetchQueriedVideos,
  } = useGetVideosQuery({ title: relatedVideoSearch });
  const [deleteVideo, {
    isSuccess: isDeleteVideoSuccess,
    isError: isDeleteVideoError,
    isLoading: isDeleteVideoLoading,
    error: deleteVideoError,
  }] = useDeleteVideoMutation();
  const [updateVideo, {
    isSuccess: isUpdateVideoSuccess,
    isLoading: isUpdateVideoLoading,
    isError: isUpdateVideoError,
    error: updateVideoError,
    reset: resetUpdateVideo,
  }] = useUpdateVideoMutation();
  // These are the same useUpdateVideoMutation as above, but is separated out
  // to avoid errors and success checks from interfering with each other
  const [removeRelatedVideo, {
    isSuccess: isRemoveRelatedVideoSuccess,
    isLoading: isRemoveRelatedVideoLoading,
    error: removeRelatedVideoError,
    reset: resetRemoveRelatedVideo,
  }] = useUpdateVideoMutation();
  const [removeResource, {
    isSuccess: isRemoveResourceSuccess,
    isLoading: isRemoveResourceLoading,
    error: removeResourceError,
    reset: resetRemoveResource,
  }] = useUpdateVideoMutation();

  // Update videos query when the search term changes
  useEffect(() => {
    refetchQueriedVideos();
  }, [relatedVideoSearch]);

  // Update page title
  useEffect(() => {
    if (video?.data.title) {
      document.title = `${video?.data.title} | Cognito Videos`;
    }
  }, [video]);

  const topicObj = video?.data.topic.split('#') || [];
  // const subject = topicObj[0];
  const category = topicObj[1];
  const topic = topicObj[2];

  useEffect(() => {
    if (isDeleteVideoSuccess) {
      // Navigate up a level after a topic is deleted
      navigate(`/admin/videos/${subject}/${category}/${topic}`);
    }
  }, [isDeleteVideoSuccess]);

  useSuccess(isDeleteVideoSuccess, 'Video deleted');
  useError(isDeleteVideoError, deleteVideoError);
  useError(isUpdateVideoError, updateVideoError);

  const addRelatedVideo = async (targetSubject: string, targetVideoId: string) =>
    updateVideo({
      subject,
      videoId,
      attributes: {
        relatedVideos: [
          ...(video?.data.relatedVideos || [])
            .map((x) => ({ subject: x.subject, videoId: x.videoId })),
          {
            subject: targetSubject,
            videoId: targetVideoId,
          },
        ],
      },
    });

  const resetModal = async () => {
    await resetUpdateVideo();
    setNewResourceForm(INITIAL_RESOURCE_FORM);
    await resetRemoveResource();
    await resetRemoveRelatedVideo();
  };

  // Close modal after resource is added
  useEffect(() => {
    if (isUpdateVideoSuccess && newResourceFormOpen) {
      setNewResourceFormOpen(false);
      resetModal();
    }
  }, [isUpdateVideoSuccess]);

  return (
    <PageContainer>
      {!isLoading && video?.data && (
        <>
          <Breadcrumbs
            title={video?.data.title}
            path={[
              {
                label: video?.data.path[0].label || 'Videos',
                href: `/admin/videos/${subject}`,
              },
              {
                label: video?.data.path[1].label,
                href: `/admin/videos/${subject}/${category}`,
              },
              {
                label: video?.data.path[2].label,
                href: `/admin/videos/${subject}/${category}/${topic}`,
              },
            ]}
          />
          <ContentWrapper title="Video Preview">
            <ColumnContainer stack={!isMd}>
              <VideoColumn>
                <YouTubeVideo
                  videoId={video?.data?.youtubeId}
                  autoplay={false}
                />
              </VideoColumn>
              <DetailsColumn>
                <CommitForm
                  data={video?.data}
                  metadata={video?.metadata}
                  fields={{
                    title: {
                      xs: 12,
                      md: 12,
                    },
                    description: {
                      xs: 12,
                      md: 12,
                    },
                    host: {
                      xs: 12,
                      md: 12,
                    },
                    youtubeId: {
                      xs: 12,
                      md: 12,
                    },
                  }}
                  onSubmit={async (values) => updateVideo({
                    subject,
                    videoId,
                    attributes: values,
                  })}
                  isSubmitLoading={isUpdateVideoLoading}
                  autoSubmit
                />
              </DetailsColumn>
            </ColumnContainer>
          </ContentWrapper>
          <ContentWrapper title="Actions">
            <Stack direction="row" spacing={1}>
              <ActionButton
                color="error"
                size="medium"
                onClick={() => setDeleteModalOpen(true)}
                startIcon={(<DeleteRounded />)}
              >
                Delete
              </ActionButton>
              <CopyButton
                size="medium"
                copyText={`${window.location.origin}/video/${subject}/${videoId}`}
                startIcon={(<LinkRounded />)}
                buttonLabel="Copy Link"
              />
            </Stack>
          </ContentWrapper>
          <ContentWrapper title="Related Videos">
            <ContentGroupsList
              columns={[
                {
                  field: 'subject',
                  headerName: 'Subject',
                  width: 180,
                  renderCell: ({ value }) => SUBJECTS[value]?.label,
                },
                {
                  field: 'title',
                  headerName: 'Title',
                  width: 360,
                },
              ]}
              actions={{
                create: {
                  fn: async () => setAddRelatedVideoModalOpen(true),
                  label: 'Add',
                },
                delete: {
                  fn: async (params) => removeRelatedVideo({
                    subject,
                    videoId,
                    attributes: {
                      relatedVideos: params?.data
                        .filter((x: ShortVideo) => !params?.selectedRows?.includes(x.videoId))
                        .map((x: ShortVideo) => (_.pick(x, ['subject', 'videoId']))),
                    },
                  }),
                  label: 'Remove',
                  isSuccess: isRemoveRelatedVideoSuccess,
                  isLoading: isRemoveRelatedVideoLoading,
                  error: removeRelatedVideoError,
                  color: 'error',
                },
                reorder: {
                  fn: async (params) => updateVideo({
                    subject,
                    videoId,
                    attributes: {
                      relatedVideos: params?.orderedData.map((x: ShortVideo) => (_.pick(x, ['subject', 'videoId']))),
                    },
                  }),
                  label: 'Reorder',
                  isSuccess: isUpdateVideoSuccess,
                  error: updateVideoError,
                },
              }}
              data={(video?.data.relatedVideos || [])
                .map((x: ShortVideo) => ({ ...x, id: x.videoId }))}
              loading={isLoading}
              getRowHref={(row: ShortVideo) => `/admin/video/${row.subject}/${row.videoId}`}
              checkboxSelection
            />
          </ContentWrapper>
          <ContentWrapper title="Resources">
            <ContentGroupsList
              columns={[
                {
                  field: 'label',
                  headerName: 'Label',
                  width: 200,
                },
                {
                  field: 'href',
                  headerName: 'Link',
                  width: 300,
                },
              ]}
              actions={{
                delete: {
                  fn: async (params) => removeResource({
                    subject,
                    videoId,
                    attributes: {
                      resources: params?.data
                        .filter((x: Resource) => !params?.selectedRows?.includes(x.label)),
                    },
                  }),
                  label: 'Remove',
                  isSuccess: isRemoveResourceSuccess,
                  isLoading: isRemoveResourceLoading,
                  error: removeResourceError,
                  color: 'error',
                },
                reorder: {
                  fn: async (params) => updateVideo({
                    subject,
                    videoId,
                    attributes: {
                      resources: params?.orderedData,
                    },
                  }),
                  label: 'Reorder',
                  isSuccess: isUpdateVideoSuccess,
                  isLoading: isUpdateVideoLoading,
                  error: updateVideoError,
                },
                create: {
                  fn: async () => setNewResourceFormOpen(true),
                  label: 'Add',
                },
              }}
              data={(video?.data.resources || []).map((x: Resource) => ({ ...x, id: x.label }))}
              loading={isLoading}
              checkboxSelection
              getRowHref={(row: Resource) => row.href}
              rowHrefTarget="_blank"
            />
          </ContentWrapper>
          <DetailsModal
            open={newResourceFormOpen}
            handleClose={() => setNewResourceFormOpen(false)}
            error={updateVideoError}
            title="Add New Resource"
            fields={{
              label: {
                editable: true,
                type: 'text',
                label: 'Unique Label',
              },
              href: {
                editable: true,
                type: 'text',
                label: 'Link',
              },
            }}
            values={newResourceForm}
            buttons={[{
              fn: (values?: Values) => {
                updateVideo({
                  subject,
                  videoId,
                  attributes: {
                    resources: [...(video?.data.resources || []), values],
                  },
                });
                setNewResourceFormOpen(false);
              },
              label: 'Add',
              loading: isUpdateVideoLoading,
            }]}
          />
          <Modal
            open={deleteModalOpen}
            handleClose={() => setDeleteModalOpen(false)}
            title="Are you sure?"
            description="This will permanently delete this video."
            buttons={[
              {
                label: 'Delete',
                onClick: () => deleteVideo({
                  subject, category, videoId,
                }),
                color: 'error',
                loading: isDeleteVideoLoading,
              },
            ]}
          />
          <AdditionModal
            title="Add Related Video"
            searchPlaceholder="Video"
            open={addRelatedVideoModalOpen}
            handleClose={() => {
              setAddRelatedVideoModalOpen(false);
            }}
            onAdd={async (v) => addRelatedVideo(v.subject, v.videoId)}
            isLoading={isUpdateVideoLoading}
            isSuccess={isUpdateVideoSuccess}
            isError={isUpdateVideoError}
            error={(updateVideoError as any)?.data?.message}
            closeNotifs={resetUpdateVideo}
            searchFieldProps={{
              options: queriedVideos?.data.filter((x) =>
                !video?.data.relatedVideos?.some((y) => y.videoId === x.videoId)) || [],
              getOptionLabel: (v) => `${v?.title}${v?.path?.[0]?.label && ` (${v?.path?.[0]?.label})`}`,
              getOptionKey: (v) => v?.videoId,
              onSearch: (str) => setRelatedVideoSearch(str),
            }}
          />
        </>
      )}
    </PageContainer>
  );
};

export default AdminVideoPage;
